'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { pushEvent } from '@/lib/analytics/gtag'
import { PAGE_VIEW, ATTRIBUTION_CAPTURED } from '@/lib/analytics/events'
import { deriveContextFromPath } from '@/lib/analytics/page-context'
import { pathnameOnly } from '@/lib/analytics/path-metadata'
import {
  buildAttributionCapture,
  campaignFingerprint,
  clickIdDerivedSource,
  saveAttribution,
} from '@/lib/attribution'
import { readExplicitAdvertisingConsent } from '@/lib/consent/advertising-consent'
import {
  AdvertisingMeasurement,
  type AdvertisingMeasurementConfig,
  useAdvertisingMeasurementGrant,
} from './AdvertisingMeasurement'

export function AnalyticsProvider({ config }: { config: AdvertisingMeasurementConfig }) {
  const measurementGranted = useAdvertisingMeasurementGrant()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const lastPathRef = useRef<string | null>(null)
  const lastAttributionKeyRef = useRef<string | null>(null)
  // False until the first attribution pass after a full page load — that pass
  // is the "landing" that contributes first-touch referrer/landingPath (#80).
  const landingCapturedRef = useRef(false)

  useEffect(() => {
    if (!measurementGranted || !config.gtmId || !pathname) return
    const search = searchParams?.toString()
    const fullPath = search ? `${pathname}?${search}` : pathname
    const context = deriveContextFromPath(pathname)

    const fire = () => {
      pushEvent(PAGE_VIEW, {
        ...context,
        ...(lastPathRef.current ? { last_path: lastPathRef.current } : {}),
      })
      lastPathRef.current = pathnameOnly(fullPath)
    }

    // Initial page load: wait until all resources are done so the dataLayer push
    // doesn't trigger GTM tag evaluation while LCP-critical work is still running.
    // SPA navigations: readyState is already 'complete', so fire immediately.
    if (document.readyState === 'complete') {
      fire()
    } else {
      window.addEventListener('load', fire, { once: true })
      return () => window.removeEventListener('load', fire)
    }
  }, [config.gtmId, measurementGranted, pathname, searchParams])

  // Capture attribution from any landing page URL, not just /book: rep/UTM
  // params, ad-platform click IDs (#79 — Google auto-tagging sends ?gclid=
  // with no utm_*), and first-touch referrer + landing path (#80 — so
  // untagged visits are no longer a black hole). The landing capture fires
  // once per full page load; campaign captures deduplicate by param
  // fingerprint so SPA navigations don't re-fire. First-touch vs
  // last-write-wins semantics live in saveAttribution.
  useEffect(() => {
    if (!searchParams) return
    const capture = buildAttributionCapture(searchParams, {
      referrer: document.referrer,
      origin: window.location.origin,
      path: window.location.pathname + window.location.search,
      isLanding: !landingCapturedRef.current,
      advertisingConsent: readExplicitAdvertisingConsent(document.cookie),
    })
    landingCapturedRef.current = true
    if (!capture) return

    const fingerprint = campaignFingerprint(capture)
    if (fingerprint !== null && fingerprint === lastAttributionKeyRef.current) return
    saveAttribution(capture)
    if (fingerprint === null) return // plain landing — nothing campaign-shaped to announce
    lastAttributionKeyRef.current = fingerprint

    const eventParams: { rep_code?: string; lead_source?: string } = {}
    if (capture.repCode) eventParams.rep_code = capture.repCode
    const source = capture.utmSource ?? clickIdDerivedSource(capture.clickIds)?.source
    if (source) eventParams.lead_source = source
    pushEvent(ATTRIBUTION_CAPTURED, eventParams)
  }, [searchParams])

  return (
    <>
      <AdvertisingMeasurement config={config} granted={measurementGranted} />
    </>
  )
}
